import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useUserStore from "@store/useUserStore";
import { getOpenClassInfo, createClass, createPracticeClass } from "api/admin";
import {
    getLocationList,
    getClassScheduleDetail,
    getPracticeScheduleDetail,
    getAssociationList,
} from "api/class";
import { editClassSchedule, editPracticeSchedule } from "api/attendance";
import { sportCategories } from "assets/data/mainItems";
import { PRACTICE_CATEGORIES } from "pages/Admin/regist";
import { PREMIUM_FIXED_PRICE, PREMIUM_DEFAULT_SUB_TITLE } from "constants/lessonRequest";
import {
    getInitialTimeSetting,
    parseTimeStringToDate,
    generateTimeSlots,
} from "utils/classUtils";
import useDeleteSchedule from "hooks/useDeleteSchedule";
import dayjs from "dayjs";
import {DEFAUTL_COMMENT} from '../pages/reservation/ReservationDetail'

const PRACTICE_SPORTS = Object.keys(PRACTICE_CATEGORIES);

/**
 * 서버 4xx 에러 응답(res.data)에서 사람이 읽을 수 있는 메시지를 추출한다.
 * 예) { non_field_errors: ["시간 겹침 불가."] } → "시간 겹침 불가."
 *     { date: ["필수 항목입니다."] } → "date: 필수 항목입니다."
 *     { detail: "...", conflicts: [{ date, reason }] } → 날짜별 충돌 사유 나열
 */
const extractErrorMessage = (data, fallback) => {
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (Array.isArray(data)) {
        const joined = data
            .map((item) => extractErrorMessage(item, ""))
            .filter(Boolean)
            .join(" ");
        return joined || fallback;
    }
    if (typeof data === "object") {
        const messages = Object.entries(data)
            .map(([field, value]) => {
                // 다중 날짜 일괄 생성 충돌: [{ date, reason }, ...]
                if (field === "conflicts" && Array.isArray(value)) {
                    return value
                        .map((c) =>
                            [c?.date, c?.reason].filter(Boolean).join(" - ")
                        )
                        .filter(Boolean)
                        .join("\n");
                }
                const message = extractErrorMessage(value, "");
                if (!message) return "";
                // non_field_errors / detail 등 폼 전체 에러는 필드명 없이 메시지만 노출
                return field === "non_field_errors" || field === "detail"
                    ? message
                    : `${field}: ${message}`;
            })
            .filter(Boolean);
        return messages.length ? messages.join("\n") : fallback;
    }
    return fallback;
};

/**
 * CreateClass 폼 상태 관리 커스텀 훅
 */
const useCreateClassForm = ({ showSuccess, showError }) => {
    const userInfo = useUserStore((state) => state.userInfo);
    const { id: classId, type: classRouteType } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(classId);

    // Refs
    const startTimePickerRef = useRef(null);
    const endTimePickerRef = useRef(null);
    const datePickerRef = useRef(null);
    const originalDataRef = useRef(null);
    const dataLoadedRef = useRef(false);

    // 상태
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [classType, setClassType] = useState("lesson");
    const [practiceSport, setPracticeSport] = useState("");
    const [datePickerState, setDatePickerState] = useState(false);
    const [showTimePickerState, setShowTimePickerState] = useState({
        start: false,
        end: false,
    });

    const [formData, setFormData] = useState({
        lesson_product: "",
        instructor: userInfo?.name,
        date: new Date(),
        start_time: getInitialTimeSetting().startTime,
        end_time: getInitialTimeSetting().endTime,
        location: "",
        capacity: 4,
        practice_capacity: 4,
        day_order: null,
        is_theory: false,
        status: "OPEN",
        create_practice_session: true,
        allowed_ticket_types: "NONE",
        pricing_type: "FREE",
        instructor_comment: "",
        note: DEFAUTL_COMMENT,
        sub_title: PREMIUM_DEFAULT_SUB_TITLE,
    });

    // 강습 일괄 개설용 다중 날짜 (create + lesson 전용). 기본값은 오늘 1개.
    const [selectedDates, setSelectedDates] = useState([new Date()]);

    const [lessonProducts, setLessonProducts] = useState([]);

    const [dataState, setDataState] = useState({
        sports: sportCategories.map((sport) => sport.title),
        locations: [],
    });

    const [associationList, setAssociationList] = useState([]);
    const [selectedAssociations, setSelectedAssociations] = useState([]);
    const [hasReservations, setHasReservations] = useState(false);
    const [confirmedCount, setConfirmedCount] = useState(0);
    const [pricingType, setPricingType] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [practiceCategory, setPracticeCategory] = useState("");

    // 시간 슬롯 메모이제이션
    const timeSlots = useMemo(() => generateTimeSlots(), []);

    const uniqueCourses = useMemo(() => {
        return lessonProducts.map((item) => ({ brand: item.brand, displayName: item.name }));
    }, [lessonProducts]);

    // lesson product 이름 → allowed_ticket_types 맵 (ref)
    const courseTicketTypeMapRef = useRef({});

    // 데이터 로딩
    useEffect(() => {
        if (dataLoadedRef.current === classId) return;
        const fetchData = async () => {
            dataLoadedRef.current = classId;
            try {
                const [classInfoRes, divingPoolsRes] = await Promise.all([
                    getOpenClassInfo(),
                    getLocationList(),
                ]);

                const locations =
                    divingPoolsRes.locations?.map((loc) => loc?.name) || [];

                // brand → allowed_ticket_types 맵 저장
                const ticketTypeMap = {};
                classInfoRes.forEach((item) => {
                    if (item.name) {
                        ticketTypeMap[item.name] = item.allowed_ticket_types || "NONE";
                    }
                });
                courseTicketTypeMapRef.current = ticketTypeMap;

                setLessonProducts(classInfoRes);
                setDataState((prevState) => ({
                    ...prevState,
                    locations: locations,
                }));

                if (isEditMode) {
                    const isPracticeEdit = classRouteType === "practice";
                    const res = isPracticeEdit
                        ? await getPracticeScheduleDetail(classId)
                        : await getClassScheduleDetail(classId);

                    if (res && res.date) {
                        const baseDate = new Date(res.date);

                        if (isPracticeEdit) {
                            setClassType("practice");
                            const title = res.title || res.name || "";
                            setPracticeSport(title);
                            setPricingType(res.pricing_type ?? null);

                            const bookings = res.current_bookings || 0;
                            setHasReservations(bookings > 0);
                            setConfirmedCount(bookings);

                            // 종목 추출: sport 필드 여러 변형 시도 → 없으면 title에서 매칭
                            let sport = res.sport || res.sport_name || "";
                            if (!sport && title) {
                                const matched = PRACTICE_SPORTS.find(s => title.startsWith(s));
                                if (matched) sport = matched;
                            }

                            // 연습반 분류 카테고리 추출 (title에서 sport 제거)
                            const category = sport && title.startsWith(sport)
                                ? title.slice(sport.length).trim()
                                : "";
                            setPracticeCategory(category);

                            setFormData((prevFormData) => ({
                                ...prevFormData,
                                lesson_product: sport,
                                instructor: res.instructor_name || userInfo?.name || "",
                                date: baseDate,
                                start_time: parseTimeStringToDate(res.start_time, baseDate),
                                end_time: parseTimeStringToDate(res.end_time, baseDate),
                                location: res.location_name || "",
                                capacity: res.capacity ?? 4,
                                status: res.status || "OPEN",
                                instructor_comment: res.note || "",
                                note: res.note || "",
                                sub_title: res.sub_title || PREMIUM_DEFAULT_SUB_TITLE,
                            }));
                            originalDataRef.current = {
                                instructor: res.instructor_name || userInfo?.name || "",
                                date: res.date,
                                start_time: res.start_time?.substring(0, 5),
                                end_time: res.end_time?.substring(0, 5),
                                location: res.location_name || "",
                                capacity: res.capacity ?? 4,
                                status: res.status || "OPEN",
                                title: res.title || "",
                                note: res.note || "",
                                sub_title: res.sub_title || PREMIUM_DEFAULT_SUB_TITLE,
                            };
                        } else {
                            const confirmed =
                                res.reservations?.confirmed?.length || 0;
                            setConfirmedCount(confirmed);
                            setHasReservations(confirmed > 0);

                            setFormData((prevFormData) => ({
                                ...prevFormData,
                                lesson_product: res.lesson_product_name || "",
                                instructor:
                                    res.instructor_name || userInfo?.name || "",
                                date: baseDate,
                                start_time: parseTimeStringToDate(
                                    res.start_time,
                                    baseDate
                                ),
                                end_time: parseTimeStringToDate(
                                    res.end_time,
                                    baseDate
                                ),
                                location: res.location_name || "",
                                capacity: res.capacity ?? 4,
                                practice_capacity: res.practice_capacity ?? 4,
                                day_order: res.day_order ?? null,
                                is_theory: res.is_theory ?? false,
                                status: res.status || "OPEN",
                                create_practice_session:
                                    res.create_practice_session ?? false,
                                allowed_ticket_types:
                                    res.allowed_ticket_types === "NONE" ||
                                    !res.allowed_ticket_types
                                        ? "REGULAR"
                                        : res.allowed_ticket_types,
                                title: res.title || "",
                                instructor_comment: res.instructor_comment || "",
                            }));

                            // 원본 데이터 저장 (변경된 값만 PATCH로 전송하기 위해)
                            originalDataRef.current = {
                                lesson_product: res.lesson_product_name || "",
                                instructor: res.instructor_name || userInfo?.name || "",
                                date: res.date,
                                start_time: res.start_time?.substring(0, 5),
                                end_time: res.end_time?.substring(0, 5),
                                location: res.location_name || "",
                                capacity: res.capacity ?? 4,
                                practice_capacity: res.practice_capacity ?? 4,
                                day_order: res.day_order ?? null,
                                is_theory: res.is_theory ?? false,
                                status: res.status || "OPEN",
                                create_practice_session: res.create_practice_session ?? false,
                                allowed_ticket_types:
                                    !res.allowed_ticket_types || res.allowed_ticket_types === "NONE"
                                        ? "NONE"
                                        : res.allowed_ticket_types,
                                title: res.title || "",
                                instructor_comment: res.instructor_comment || "",
                                associations: [],
                            };

                            // 수정 모드에서 기존 associations 데이터 불러오기
                            if (
                                res.associations &&
                                Array.isArray(res.associations)
                            ) {
                                const associationIds = res.associations.map(
                                    (assoc) =>
                                        typeof assoc === "object" ? assoc.id : assoc
                                );
                                setSelectedAssociations(associationIds);
                                handleInputChange("associations", associationIds);
                                originalDataRef.current.associations = associationIds;
                            }

                            if (
                                res.lesson_product_name &&
                                PRACTICE_SPORTS.includes(res.lesson_product_name)
                            ) {
                                setClassType("practice");
                                setPracticeSport(res.lesson_product_name);
                            } else {
                                setClassType("lesson");
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("데이터 로딩 중 오류 발생:", error);
                setDataState((prevState) => ({
                    ...prevState,
                    courses: [],
                    locations: [],
                }));
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps

        getAssociationList().then((res) => {
            setAssociationList(res);
        });
    }, [classId]); // eslint-disable-line react-hooks/exhaustive-deps

    // 클릭 외부 감지
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                startTimePickerRef.current &&
                !startTimePickerRef.current.contains(event.target)
            ) {
                setShowTimePickerState((prev) => ({ ...prev, start: false }));
            }
            if (
                endTimePickerRef.current &&
                !endTimePickerRef.current.contains(event.target)
            ) {
                setShowTimePickerState((prev) => ({ ...prev, end: false }));
            }
            if (
                datePickerRef.current &&
                !datePickerRef.current.contains(event.target)
            ) {
                setDatePickerState(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // 핸들러들
    const handleInputChange = useCallback((field, value) => {
        setFormData((prev) => {
            const updatedFormData = { ...prev, [field]: value };

            if (field === "start_time" && value) {
                const startTime = new Date(value);
                const autoEndTime = new Date(
                    startTime.getTime() + 3 * 60 * 60 * 1000
                );
                updatedFormData.end_time = autoEndTime;
            }

            // associations 필드가 업데이트될 때 체크된 협회 id 배열로 설정
            if (field === "associations") {
                updatedFormData.associations = value;
            }

            return updatedFormData;
        });
    }, []);

    // 협회 체크박스 변경 핸들러
    const handleAssociationChange = useCallback(
        (associationId, isChecked) => {
            setSelectedAssociations((prev) => {
                let updated;
                if (isChecked) {
                    // 체크된 경우 추가
                    updated = [...prev, associationId];
                } else {
                    // 체크 해제된 경우 제거
                    updated = prev.filter((id) => id !== associationId);
                }
                // handleInputChange에 체크된 협회 id 배열 전달
                handleInputChange("associations", updated);
                return updated;
            });
        },
        [handleInputChange]
    );

    const handleCourseChange = useCallback((e) => {
        const value = e.target.value;
        const rawTicketType = courseTicketTypeMapRef.current[value] || "NONE";
        const autoTicketType = rawTicketType === "NONE" ? "REGULAR" : rawTicketType;
        setFormData((prev) => ({
            ...prev,
            lesson_product: value,
            allowed_ticket_types: value ? autoTicketType : prev.allowed_ticket_types,
        }));
        setPracticeSport("");
        setPracticeCategory("");
    }, []);

    const handleTicketTypeChange = useCallback((e) => {
        setFormData((prev) => ({
            ...prev,
            allowed_ticket_types:
                e.target.value === "" ? "NONE" : e.target.value,
        }));
    }, []);

    const toggleDatePicker = useCallback(() => {
        setDatePickerState((prev) => !prev);
    }, []);

    // react-datepicker selectsMultiple → 선택된 Date 배열(또는 null) 처리
    const handleDatesChange = useCallback((dates) => {
        if (Array.isArray(dates)) {
            setSelectedDates(dates);
        } else {
            setSelectedDates(dates ? [dates] : []);
        }
    }, []);

    // 칩에서 특정 날짜 선택 해제
    const removeSelectedDate = useCallback((target) => {
        const key = dayjs(target).format("YYYY-MM-DD");
        setSelectedDates((prev) =>
            prev.filter((d) => dayjs(d).format("YYYY-MM-DD") !== key)
        );
    }, []);

    const toggleTimePicker = useCallback((type) => {
        setShowTimePickerState((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    }, []);

    const handleTitleChange = useCallback((e) => {
        setFormData((prev) => ({ ...prev, title: e.target.value + " 스페셜 트레이닝" }));
    }, []);

    const handleSave = useCallback(async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
        if (classType === "lesson") {
            // eslint-disable-next-line no-unused-vars
            const { pricing_type, date, ...lessonData } = formData;
            // 선택한 날짜들을 YYYY-MM-DD 배열로 변환 (한 요청으로 일괄 개설, 서버에서 원자성 처리)
            const dates = selectedDates.map((d) =>
                dayjs(d).format("YYYY-MM-DD")
            );
            if (dates.length === 0) {
                showError("날짜를 1개 이상 선택해주세요.");
                return;
            }
            const res = await createClass({ ...lessonData, dates });
            if (res.status >= 400) {
                const errorMessage =
                    res.status < 500
                        ? extractErrorMessage(res.data, "입력값을 확인해주세요.")
                        : "서버 오류 : 수업을 생성하는데 실패했습니다.";
                showError(errorMessage);
            } else {
                const count = res.data?.count ?? dates.length;
                showSuccess(`${count}개의 수업을 생성했습니다.`);
            }
        } else {
            const practiceData = {
                instructor: formData.instructor,
                date: formData.date,
                start_time: formData.start_time,
                end_time: formData.end_time,
                location: formData.location,
                capacity: formData.capacity,
                sport: formData.lesson_product,
                status: "OPEN",
                title: practiceSport,
                pricing_type: formData.pricing_type,
                note:formData.note,
                // PAID 는 CreateClass 분류 select 에서 "프리미엄 트레이닝" 선택 시에만 설정된다.
                ...(formData.pricing_type === "PAID" && {
                    price: PREMIUM_FIXED_PRICE,
                    sub_title: formData.sub_title?.trim() || PREMIUM_DEFAULT_SUB_TITLE,
                }),
                ...(formData.instructor_comment && { note: formData.instructor_comment }),
            };
            const res = await createPracticeClass(practiceData);
            if (res.status >= 400) {
                const errorMessage =
                    res.status < 500
                        ? extractErrorMessage(res.data, "입력값을 확인해주세요.")
                        : "서버 오류 : 연습반을 생성하는데 실패했습니다.";
                showError(errorMessage);
            } else {
                showSuccess("연습반을 생성했습니다.");
            }
        }
        } finally {
            setIsSaving(false);
        }
    }, [classType, formData, practiceSport, selectedDates, showSuccess, showError, isSaving]);


    const handleEdit = useCallback(async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
        if (classType === "lesson") {
            // eslint-disable-next-line no-unused-vars
            const { pricing_type, ...lessonFormData } = formData;
            const editableKeys = [
                "instructor",
                "date",
                "start_time",
                "end_time",
                "location",
                "capacity",
                "status",
                "lesson_product",
                "day_order",
                "is_theory",
                "allowed_ticket_types",
                "title",
            ];
            const currentParams = editableKeys.reduce((params, key) => {
                if (Object.prototype.hasOwnProperty.call(originalDataRef.current || {}, key)) {
                    params[key] = lessonFormData[key];
                }
                return params;
            }, {});

            if (Object.prototype.hasOwnProperty.call(currentParams, "allowed_ticket_types")) {
                currentParams.allowed_ticket_types =
                    formData.allowed_ticket_types === "NONE" ||
                    formData.allowed_ticket_types === "REGULAR"
                        ? "NONE"
                        : formData.allowed_ticket_types;
            }
            if (Object.prototype.hasOwnProperty.call(currentParams, "start_time")) {
                currentParams.start_time = dayjs(formData.start_time).format("HH:mm");
            }
            if (Object.prototype.hasOwnProperty.call(currentParams, "end_time")) {
                currentParams.end_time = dayjs(formData.end_time).format("HH:mm");
            }
            if (Object.prototype.hasOwnProperty.call(currentParams, "date")) {
                currentParams.date = dayjs(formData.date).format("YYYY-MM-DD");
            }

            // 변경된 값만 추출
            const changedParams = {};
            const original = originalDataRef.current;
            if (original) {
                Object.keys(currentParams).forEach((key) => {
                    if (
                        JSON.stringify(currentParams[key]) !==
                        JSON.stringify(original[key])
                    ) {
                        changedParams[key] = currentParams[key];
                    }
                });
            } else {
                Object.assign(changedParams, currentParams);
            }

            const res = await editClassSchedule(classId, changedParams);
            if (res.status >= 400) {
                const errorField = Object.entries(res.data);
                const msg = errorField.map(([field, txt])=> field+' - '+txt).join('\n')
                showError(msg);
            } else {
                showSuccess("수업을 수정했습니다.");
            }
        } else {
            // 연습반 수정
            const currentParams = {
                instructor: formData.instructor,
                date: dayjs(formData.date).format("YYYY-MM-DD"),
                start_time: dayjs(formData.start_time).format("HH:mm"),
                end_time: dayjs(formData.end_time).format("HH:mm"),
                location: formData.location,
                capacity: formData.capacity,
                title: practiceSport,
                note: formData.note,
                // 부제목은 프리미엄 트레이닝만 사용한다. 예약자가 있어도 수정 가능(BE 허용).
                ...(pricingType === "PAID" && {
                    sub_title: formData.sub_title?.trim() || PREMIUM_DEFAULT_SUB_TITLE,
                }),
            };

            // 변경된 값만 추출 (PATCH 방식)
            const changedParams = {};
            const original = originalDataRef.current;
            if (original) {
                Object.keys(currentParams).forEach((key) => {
                    if (JSON.stringify(currentParams[key]) !== JSON.stringify(original[key])) {
                        changedParams[key] = currentParams[key];
                    }
                });
            } else {
                Object.assign(changedParams, currentParams);
            }

            if (Object.keys(changedParams).length === 0) {
                showError("변경된 내용이 없습니다.");
                return;
            }

            const res = await editPracticeSchedule(classId, changedParams);
            if (res?.status === 200) {
                showSuccess("연습반이 수정되었습니다.");
                navigate(-1);
            } else {
                showError(
                    res?.status >= 400 && res.status < 500
                        ? extractErrorMessage(res.data, "연습반을 수정하는데 실패했습니다.")
                        : "연습반을 수정하는데 실패했습니다."
                );
            }
        }
        } finally {
            setIsSaving(false);
        }
    }, [classType, formData, practiceSport, pricingType, classId, showSuccess, showError, navigate, isSaving]);

    const { handleDelete: deleteSchedule } = useDeleteSchedule();

    const handleDelete = useCallback(async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            await deleteSchedule(classId, classType, {
                onSuccess: () => navigate(-1),
            });
        } finally {
            setIsSaving(false);
        }
    }, [classId, classType, isSaving, navigate, deleteSchedule]);

    return {
        // Refs
        startTimePickerRef,
        isLoading,
        endTimePickerRef,
        datePickerRef,
        // 상태
        classType,
        setClassType,
        practiceSport,
        setPracticeSport,
        formData,
        dataState,
        isEditMode,
        hasReservations,
        confirmedCount,
        pricingType,
        isSaving,
        practiceCategory,
        setPracticeCategory,
        datePickerState,
        setDatePickerState,
        selectedDates,
        showTimePickerState,
        setShowTimePickerState,
        associationList,
        // 메모이제이션된 데이터
        timeSlots,
        uniqueCourses,
        // 핸들러
        handleInputChange,
        handleCourseChange,
        handleTicketTypeChange,
        toggleDatePicker,
        handleDatesChange,
        removeSelectedDate,
        toggleTimePicker,
        handleSave,
        handleEdit,
        handleDelete,
        handleTitleChange,
        handleAssociationChange,
        selectedAssociations,
        // 상수
        PRACTICE_SPORTS,
        PRACTICE_CATEGORIES,
    };
};

export default useCreateClassForm;
